/**
 * Auth hook — JWT in localStorage, verified against
 * `kra-photo-journal-worker /api/auth/me` on mount.
 *
 * Default: auth is OFF (`VITE_REQUIRE_AUTH != "true"`). The hook returns a
 * permanent anonymous state and never network-calls. Flip the build env var
 * + the worker's `ADMIN_AUTH_REQUIRED=true` to re-enable the invite-code flow.
 *
 * When enabled, uses TOKEN_KEY = "kra_token" (same as kra-photo-journal so
 * a single login carries over).
 */

import { useCallback, useEffect, useState } from 'react';
import { authMe } from '../lib/api.js';

const TOKEN_KEY = 'kra_token';
const REQUIRE_AUTH = import.meta.env['VITE_REQUIRE_AUTH'] === 'true';

export interface AuthState {
  name: string | null;
  token: string | null;
  role: string | null;
  staffId: string | null;
  isLoading: boolean;
}

const ANON_STATE: AuthState = {
  name: 'admin',
  token: null,
  role: 'admin',
  staffId: null,
  isLoading: false,
};

export function useAuth() {
  const [state, setState] = useState<AuthState>(
    REQUIRE_AUTH
      ? { name: null, token: null, role: null, staffId: null, isLoading: true }
      : ANON_STATE,
  );

  useEffect(() => {
    if (!REQUIRE_AUTH) return;
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
      return;
    }
    authMe(stored)
      .then((data) => {
        if (data) {
          setState({
            name: data.name,
            token: stored,
            role: data.role ?? null,
            staffId: data.staffId ?? null,
            isLoading: false,
          });
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
        }
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
      });
  }, []);

  const login = useCallback(
    (token: string, name: string, extras?: { role?: string | null; staffId?: string | null }) => {
      localStorage.setItem(TOKEN_KEY, token);
      setState({
        name,
        token,
        role: extras?.role ?? null,
        staffId: extras?.staffId ?? null,
        isLoading: false,
      });
    },
    [],
  );

  const logout = useCallback(() => {
    if (REQUIRE_AUTH) localStorage.removeItem(TOKEN_KEY);
    setState(REQUIRE_AUTH ? { name: null, token: null, role: null, staffId: null, isLoading: false } : ANON_STATE);
  }, []);

  return { ...state, login, logout };
}

export type UseAuthReturn = ReturnType<typeof useAuth>;
